import type { AnimationTarget } from "@/document/model/animation";
import type { EditorSelectionState } from "@/commands/Command";

/**
 * Build AnimationTarget[] from the current vector sub-selection within a targeting session.
 * Vertices → vectorVertex; edges → vectorEdge; selectedHandle → vectorHandle.
 * If selectedHandle is the only selection, prefer it over any vertices.
 */
export function animationTargetsFromVectorSelection(nodeId: string, selection: EditorSelectionState): AnimationTarget[] {
  const targets: AnimationTarget[] = [];

  if (selection.selectedHandle) {
    targets.push({
      kind: "vectorHandle",
      nodeId,
      edgeId: selection.selectedHandle.edgeId,
      endpoint: selection.selectedHandle.endpoint,
    });
  }

  const vertexIds = selection.selectedVertexIds?.length
    ? selection.selectedVertexIds
    : selection.selectedVertexId
      ? [selection.selectedVertexId]
      : [];
  for (const vertexId of vertexIds) {
    targets.push({ kind: "vectorVertex", nodeId, vertexId });
  }

  const edgeIds = selection.selectedEdgeIds ?? [];
  for (const edgeId of edgeIds) {
    targets.push({ kind: "vectorEdge", nodeId, edgeId });
  }

  return targets;
}

/**
 * Encode an AnimationTarget as a stable key string for object-link selection tracking.
 */
export function animationTargetKey(target: AnimationTarget): string {
  switch (target.kind) {
    case "sceneNode": return `sceneNode:${target.nodeId}`;
    case "vectorVertex": return `vectorVertex:${target.nodeId}:${target.vertexId}`;
    case "vectorHandle": return `vectorHandle:${target.nodeId}:${target.edgeId}:${target.endpoint}`;
    case "vectorEdge": return `vectorEdge:${target.nodeId}:${target.edgeId}`;
    case "animationNode": return `animationNode:${target.nodeId}`;
  }
}

/**
 * Encode an object-link connection key incorporating the target identity, anim node id, pin, and index.
 */
export function objectLinkKey(target: AnimationTarget, animNodeId: string, pin: string, index?: number): string {
  const targetPart = animationTargetKey(target);
  return index !== undefined ? `${targetPart}:${animNodeId}:${pin}:${index}` : `${targetPart}:${animNodeId}:${pin}`;
}
